import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import MovieItem from '../components/MovieItem';
import { FaSearch, FaTimes } from 'react-icons/fa';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';

export default function Search() {
    const [movies, setMovies] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchFilters, setSearchFilters] = useState({
        genre: ''
    });
    const navigate = useNavigate();
    const location = useLocation();

    // Define genre options as in UploadMovie
    const genreOptions = [
        "Action", "Horror", "Series", "Adventure", "Love Story", "Comedy", "Crime",
        "Family", "Sci-Fi", "Romance", "Kungu-Fu", "Drama", "Sport", "Thriller",
        "Animation", "Documentaries", "NollyWood", "BollyWood", "Ugandan", "Kenyan", 
        "Luganda-Translated", "Trailers"
    ];

    // Extract search query from URL (for desktop header search)
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const query = queryParams.get('q') || '';
        setSearchInput(query);
    }, [location.search]);

    // Fetch movies on mount
    useEffect(() => {
        async function fetchData() {
            try {
                // Fetch Movies
                const moviesRef = collection(db, "movies");
                const q = query(moviesRef, orderBy("timestamp", "desc"));
                const querySnap = await getDocs(q);
                const moviesData = querySnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setMovies(moviesData);
                setSearchResults(moviesData);

                setLoading(false);
            } catch (error) {
                console.log(error);
                toast.error("Could not fetch data");
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    // Update search results when search input or filters change
    useEffect(() => {
        if (movies) {
            handleSearch();
        }
    }, [searchInput, searchFilters, movies]);

    const handleSearch = async () => {
        if (!movies) return;

        let results = [...movies];

        // Text search by title
        if (searchInput) {
            results = results.filter(movie =>
                movie.title.toLowerCase().includes(searchInput.toLowerCase())
            );
        }

        // Apply genre filter
        if (searchFilters.genre) {
            results = results.filter(movie =>
                movie.genre.toLowerCase() === searchFilters.genre.toLowerCase()
            );
        }

        setSearchResults(results);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setSearchFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const resetFilters = () => {
        setSearchFilters({
            genre: ''
        });
        setSearchInput('');
        navigate('/search'); // Clear the URL query parameter
        if (movies) {
            setSearchResults(movies);
        }
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        navigate(`/search?q=${encodeURIComponent(value)}`); // Update URL for desktop header
    };

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Search Input - Mobile Only */}
                <div className="md:hidden mb-8">
                    <div className="relative max-w-2xl mx-auto">
                        <input
                            type="text"
                            placeholder="Search AweitFlix Movies..."
                            className="w-full p-4 pr-12 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white placeholder-gray-400"
                            value={searchInput}
                            onChange={handleSearchInputChange}
                        />
                        {searchInput ? (
                            <button
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-pink-500"
                                onClick={() => {
                                    setSearchInput('');
                                    navigate('/search');
                                }}
                            >
                                <FaTimes className="text-xl" />
                            </button>
                        ) : (
                            <button
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-pink-500"
                            >
                                <FaSearch className="text-xl" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters Section */}
                <div className={`mt-6 p-6 bg-gray-800 border rounded-lg max-w-2xl mx-auto ${searchFilters.genre ? 'border-pink-500' : 'border-gray-600'}`}>
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block mb-2 font-medium text-gray-400">Movie Genre</label>
                            <select
                                name="genre"
                                value={searchFilters.genre}
                                onChange={handleFilterChange}
                                className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white"
                            >
                                <option value="">All Genres</option>
                                {genreOptions.map((genre) => (
                                    <option key={genre} value={genre}>
                                        {genre}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                        <button
                            onClick={resetFilters}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>

                {/* Results Section */}
                <div className="mt-8">
                    <h2 className="text-2xl font-semibold mb-6 text-white">
                        {searchResults.length} {searchResults.length === 1 ? 'Result' : 'Results'} Found
                    </h2>

                    {searchResults.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
                            {searchResults.map((movie) => (
                                <div key={movie.id} className="movie-item-wrapper">
                                    <MovieItem movie={movie} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-xl font-medium mb-4 text-gray-300">No movies found matching your search criteria</p>
                            <button
                                onClick={resetFilters}
                                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
                            >
                                Clear Search & Filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}